import React, { useState } from 'react'
import { ChatMessage as ChatMessageType } from '../../types/chat'
import { useAuth } from '../../contexts/AuthContext'

interface ChatMessageProps {
  message: ChatMessageType
  isAdmin?: boolean
  highlightQuery?: string
  onDelete?: (messageId: string) => void
  onEdit?: (messageId: string, content: string) => Promise<void>
}

/**
 * Highlight matching substrings in text, case-insensitive.
 * Returns an array of React nodes with matches wrapped in <mark>.
 */
function highlightText(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text

  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escaped})`, 'gi')
  const parts = text.split(regex)

  if (parts.length === 1) return text

  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} className="bg-yellow-200 text-inherit rounded-sm px-0.5">{part}</mark>
    ) : (
      <span key={i}>{part}</span>
    )
  )
}

export default function ChatMessage({ message, isAdmin, highlightQuery, onDelete, onEdit }: ChatMessageProps) {
  const { user } = useAuth()
  const isOwnMessage = user?.id === message.user_id
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState(message.content)
  const [editLoading, setEditLoading] = useState(false)

  const isWithin30Min = new Date(message.created_at).getTime() > Date.now() - 30 * 60 * 1000

  // Admins can always delete/edit; regular users only their own within 30 min
  const canDelete = isAdmin || (isOwnMessage && isWithin30Min)
  const canEdit = isAdmin || (isOwnMessage && isWithin30Min)
  const isEdited = message.updated_at !== message.created_at

  const handleEditSubmit = async () => {
    if (!onEdit || !editContent.trim() || editContent.trim() === message.content) {
      setIsEditing(false)
      setEditContent(message.content)
      return
    }
    setEditLoading(true)
    try {
      await onEdit(message.id, editContent.trim())
      setIsEditing(false)
    } catch {
      // Keep editing state open on error
    } finally {
      setEditLoading(false)
    }
  }

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleEditSubmit()
    } else if (e.key === 'Escape') {
      setIsEditing(false)
      setEditContent(message.content)
    }
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const isToday = date.toDateString() === now.toDateString()

    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }

    return date.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className={`group flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'} mb-3`}>
      {/* Author name (only for others' messages) */}
      {!isOwnMessage && (
        <span className="text-xs text-gray-500 mb-1 ml-1">
          {message.author_name || 'Unknown User'}
        </span>
      )}

      <div className="flex items-end gap-1">
        {/* Action buttons (before bubble for own messages) */}
        {isOwnMessage && (canEdit || canDelete) && !isEditing && (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
            {canEdit && onEdit && (
              <button
                onClick={() => { setEditContent(message.content); setIsEditing(true) }}
                className="p-1 text-gray-400 hover:text-blue-500"
                title="Edit message"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            )}
            {canDelete && onDelete && (
              <button
                onClick={() => onDelete(message.id)}
                className="p-1 text-gray-400 hover:text-red-500"
                title="Delete message"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Message bubble */}
        <div
          className={`
            max-w-xs sm:max-w-sm md:max-w-md px-3 py-2 rounded-lg
            ${isOwnMessage
              ? 'bg-primary text-white rounded-br-none'
              : 'bg-gray-100 text-gray-900 rounded-bl-none'
            }
          `}
        >
          {isEditing ? (
            <div className="flex flex-col gap-1">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                onKeyDown={handleEditKeyDown}
                disabled={editLoading}
                className="text-sm w-full bg-white text-gray-900 rounded px-2 py-1 resize-none focus:outline-none focus:ring-1 focus:ring-purple-500"
                rows={2}
                autoFocus
              />
              <div className="flex gap-1 justify-end">
                <button
                  onClick={() => { setIsEditing(false); setEditContent(message.content) }}
                  className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSubmit}
                  className="text-xs px-2 py-0.5 rounded bg-white text-primary hover:bg-gray-100"
                  disabled={editLoading || !editContent.trim()}
                >
                  {editLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-sm whitespace-pre-wrap break-words">
              {highlightQuery ? highlightText(message.content, highlightQuery) : message.content}
            </p>
          )}
        </div>

        {/* Action buttons (after bubble for others' messages) */}
        {!isOwnMessage && (canEdit || canDelete) && !isEditing && (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
            {canEdit && onEdit && (
              <button
                onClick={() => { setEditContent(message.content); setIsEditing(true) }}
                className="p-1 text-gray-400 hover:text-blue-500"
                title="Edit message"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            )}
            {canDelete && onDelete && (
              <button
                onClick={() => onDelete(message.id)}
                className="p-1 text-gray-400 hover:text-red-500"
                title="Delete message"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Timestamp + edited indicator */}
      <span className={`text-xs text-gray-400 mt-1 ${isOwnMessage ? 'mr-1' : 'ml-1'}`}>
        {formatTime(message.created_at)}{isEdited && ' (edited)'}
      </span>
    </div>
  )
}

